
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

function isTokenValid(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }

  try {
    const parts = token.split('.');

    if (parts.length !== 3) {
      return false;
    }

    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );

    // Tokens without an expiration are not accepted by this check.
    if (typeof payload.exp !== 'number') {
      return false;
    }

    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

function getInitialSession() {
  const token = localStorage.getItem('token');
  let user = null;

  try {
    const savedUser = localStorage.getItem('user');
    user = savedUser ? JSON.parse(savedUser) : null;
  } catch {
    clearStoredSession();
    return { token: null, user: null };
  }

  if (!isTokenValid(token) || !user || !user.role) {
    clearStoredSession();
    return { token: null, user: null };
  }

  return { token, user };
}

export function AuthProvider({ children }) {
  const [initialSession] = useState(getInitialSession);

  const [token, setToken] = useState(initialSession.token);
  const [user, setUser] = useState(initialSession.user);

  const login = useCallback(async (username, password) => {
    const result = await authService.login(username, password);

    if (!result.token || !result.user || !isTokenValid(result.token)) {
      throw new Error('Login returned an invalid or expired token.');
    }

    localStorage.setItem('token', result.token);
    localStorage.setItem('user', JSON.stringify(result.user));

    setToken(result.token);
    setUser(result.user);

    return result.user;
  }, []);

  const logout = useCallback(() => {
    clearStoredSession();
    setToken(null);
    setUser(null);
  }, []);

  const isAuthenticated = Boolean(
    token && user && user.role && isTokenValid(token)
  );

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated,
      login,
      logout,
    }),
    [token, user, isAuthenticated, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
